import type { History } from "history";
import type { AppDispatch } from "src/utils/redux/store";
import { Direction, Page, PageSet } from "src/pageDefinitions/types";
import { CoreReduxState } from "src/utils/redux/store";
import ACTIONS from "../actions";
import { conditionsPass, resolveSwitch } from "../conditions";
import { getPageSet, getPageSetPageIndex } from "../pageSets";
import invariant from "tiny-invariant";
import { setActivePageSet } from "src/utils/redux/slices/linearBuyflow";
import { Store } from "redux";
import { logDebug } from "src/utils/logging";

export type GoToPageOptions = {
  history: History;
  store: Store<CoreReduxState>;
  dispatch: AppDispatch;
};

export type PageSetEventHandler = (
  event: "before:goto" | "after:goto",
  params: GoToPageOptions & {
    direction: Direction;
    activePageSet: PageSet;
    fromPage: Page;
    toPage: Page;
  }
) => void;

// Event bus for allowing hooks on goto actions.
export const pageSetEvents = new Set<PageSetEventHandler>();

export function findFuturePageIndex(
  direction: Direction,
  activePageSet: PageSet,
  { history, store }: GoToPageOptions,

  pagePosition = getPageSetPageIndex(activePageSet, history.location.pathname)
) {
  const OP = direction === Direction.FORWARD ? 1 : -1;

  for (
    let nextPageIndex = pagePosition + OP * 1;
    nextPageIndex >= 0 && nextPageIndex < activePageSet.pages.length;
    nextPageIndex += OP * 1
  ) {
    const nextPage = activePageSet.pages[nextPageIndex];
    // Guard against the page failing to meet conditions.
    if (
      !nextPage.conditions ||
      conditionsPass(nextPage.conditions, store.getState(), direction)
    ) {
      return nextPageIndex;
    }
  }
  return -1;
}

/**
 * Function that is passed into pool pages that the pages trigger to progress
 * a user forwards or backwards.
 */
export async function gotoPage(
  direction: Direction,
  activePageSet: PageSet,
  gotoOptions: GoToPageOptions,
  replace?: boolean,

  pagePosition = getPageSetPageIndex(
    activePageSet,
    gotoOptions.history.location.pathname
  )
): Promise<void> {
  invariant(direction !== Direction.CURRENT, "Cannot goto current page.");

  const { history, store } = gotoOptions;

  const nextPageIndex = findFuturePageIndex(
    direction,
    activePageSet,
    gotoOptions,
    pagePosition
  );
  const nextPage = activePageSet.pages[nextPageIndex];
  if (!nextPage) {
    return;
  }

  const resolvedPage =
    "switch" in nextPage
      ? resolveSwitch(nextPage, store.getState(), direction)
      : nextPage;

  if (resolvedPage.pathname) {
    logDebug(`Goto page: ${direction} ${resolvedPage.pathname}`);

    pageSetEvents.forEach((handler) => {
      handler("before:goto", {
        direction,
        activePageSet,
        fromPage: activePageSet.pages[pagePosition],
        toPage: nextPage,
        ...gotoOptions,
      });
    });
  }

  for (const action of resolvedPage.actions || []) {
    invariant(ACTIONS[action.type], `Unknown action type: ${action.type}`);
    // eslint-disable-next-line no-await-in-loop
    await ACTIONS[action.type]({
      params: action.params,
      ...gotoOptions,
    });
  }

  if (resolvedPage.pathname) {
    // If no condition exists, then proceed to the next page.
    history[replace ? "replace" : "push"]({
      pathname: `/${resolvedPage.pathname}`,
      search: history.location.search,
      state: { direction },
    });

    pageSetEvents.forEach((handler) => {
      handler("after:goto", {
        direction,
        activePageSet,
        fromPage: activePageSet.pages[pagePosition],
        toPage: nextPage,
        ...gotoOptions,
      });
    });
  } else {
    // TODO: Test this...
    gotoPage(direction, activePageSet, gotoOptions, replace, nextPageIndex);
  }
}

export default {
  pageSet(pageSetId: string, params: GoToPageOptions) {
    const pageSet = getPageSet(pageSetId);
    invariant(pageSet, `Page set "${pageSetId}" not found.`);

    params.dispatch(setActivePageSet(pageSet));

    gotoPage(Direction.FORWARD, pageSet, params);
  },
};
